import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { search } from "@/api/endpoints";
import { itemArtworkSrc } from "@/lib/artwork";
import { formatDurationMS, formatEpisodeCode } from "@/lib/mediaFormat";
import type { Item } from "@/types/api";

interface TopbarSearchProps {
  focusSignal: number;
  icon: ReactNode;
}

const GROUP_ORDER: Item["attributes"]["item_type"][] = [
  "show",
  "season",
  "episode",
  "movie",
  "track",
  "photo",
  "audiobook",
  "book",
];

const TYPE_LABELS: Record<Item["attributes"]["item_type"], string> = {
  movie: "Movies",
  show: "Shows",
  season: "Seasons",
  episode: "Episodes",
  track: "Music",
  photo: "Photos",
  audiobook: "Audiobooks",
  book: "Books",
};

function formatSearchMeta(item: Item): string {
  const attrs = item.attributes;
  const parts: string[] = [];

  switch (attrs.item_type) {
    case "show":
      if (attrs.year) parts.push(String(attrs.year));
      break;
    case "season":
      if (attrs.series_title) parts.push(attrs.series_title);
      if (attrs.season_title) parts.push(attrs.season_title);
      else if (attrs.season_number != null) parts.push(`Season ${attrs.season_number}`);
      break;
    case "episode": {
      const code = formatEpisodeCode(item);
      if (attrs.series_title) parts.push(attrs.series_title);
      if (code) parts.push(code);
      if (attrs.season_title) parts.push(attrs.season_title);
      if (attrs.duration_ms) parts.push(formatDurationMS(attrs.duration_ms)!);
      break;
    }
    case "movie":
    case "track":
      if (attrs.year) parts.push(String(attrs.year));
      if (attrs.duration_ms) parts.push(formatDurationMS(attrs.duration_ms)!);
      break;
    case "photo":
      if (attrs.year) parts.push(String(attrs.year));
      break;
    case "audiobook":
    case "book":
      break;
  }

  if (parts.length === 0 && attrs.year) {
    parts.push(String(attrs.year));
  }

  return parts.join(" • ");
}

export function TopbarSearch({ focusSignal, icon }: TopbarSearchProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Item[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeResultId, setActiveResultId] = useState<string | null>(null);
  const navigate = useNavigate();
  const rootRef = useRef<HTMLFormElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);
  const searchRequestSequenceRef = useRef(0);
  const resultRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const groupedResults = useMemo(
    () => GROUP_ORDER
      .map((type) => ({
        type,
        label: TYPE_LABELS[type],
        items: results.filter((item) => item.attributes.item_type === type),
      }))
      .filter((group) => group.items.length > 0),
    [results],
  );

  const flatResults = useMemo(
    () => groupedResults.flatMap((group) => group.items),
    [groupedResults],
  );

  const activeResultIndex = activeResultId
    ? flatResults.findIndex((item) => item.id === activeResultId)
    : -1;

  const closeResults = useCallback(() => {
    setOpen(false);
    setActiveResultId(null);
  }, []);

  const resetSearchState = useCallback(() => {
    setQuery("");
    setResults([]);
    closeResults();
  }, [closeResults]);

  const selectItem = useCallback((item: Item) => {
    resetSearchState();
    navigate(`/items/${item.id}`);
  }, [navigate, resetSearchState]);

  useEffect(() => {
    if (focusSignal <= 0) return;
    inputRef.current?.focus();
    if (query.trim() || results.length > 0 || loading) {
      setOpen(true);
    }
  }, [focusSignal, loading, query, results.length]);

  useEffect(() => {
    function handlePointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        closeResults();
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [closeResults]);

  useEffect(() => {
    if (flatResults.length === 0) {
      setActiveResultId(null);
      return;
    }

    if (!activeResultId || !flatResults.some((item) => item.id === activeResultId)) {
      setActiveResultId(flatResults[0]!.id);
    }
  }, [activeResultId, flatResults]);

  useEffect(() => {
    if (!activeResultId) return;
    resultRefs.current[activeResultId]?.scrollIntoView({ block: "nearest" });
  }, [activeResultId]);

  useEffect(() => {
    return () => {
      if (debounceRef.current !== null) {
        window.clearTimeout(debounceRef.current);
      }
      searchRequestSequenceRef.current += 1;
    };
  }, []);

  const doSearch = useCallback((value: string, requestSequence: number) => {
    const trimmed = value.trim();
    if (!trimmed) {
      setResults([]);
      setActiveResultId(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setOpen(true);
    search({ q: trimmed, limit: 20 })
      .then((res) => {
        if (requestSequence !== searchRequestSequenceRef.current) {
          return;
        }
        setResults(res.data);
      })
      .catch(() => {
        if (requestSequence !== searchRequestSequenceRef.current) {
          return;
        }
        setResults([]);
        setActiveResultId(null);
      })
      .finally(() => {
        if (requestSequence === searchRequestSequenceRef.current) {
          setLoading(false);
        }
      });
  }, []);

  function handleInput(value: string) {
    setQuery(value);
    setOpen(true);
    if (debounceRef.current !== null) {
      window.clearTimeout(debounceRef.current);
    }
    const requestSequence = ++searchRequestSequenceRef.current;
    if (!value.trim()) {
      setResults([]);
      setActiveResultId(null);
      setLoading(false);
      return;
    }
    debounceRef.current = window.setTimeout(() => doSearch(value, requestSequence), 300);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      closeResults();
      inputRef.current?.blur();
      return;
    }

    if (flatResults.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      const nextIndex = activeResultIndex >= 0
        ? (activeResultIndex + 1) % flatResults.length
        : 0;
      setActiveResultId(flatResults[nextIndex]?.id ?? null);
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      const nextIndex = activeResultIndex >= 0
        ? (activeResultIndex - 1 + flatResults.length) % flatResults.length
        : flatResults.length - 1;
      setActiveResultId(flatResults[nextIndex]?.id ?? null);
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const target = activeResultIndex >= 0 ? flatResults[activeResultIndex] : flatResults[0];
    if (target) {
      selectItem(target);
    }
  }

  const showResults = open && (loading || Boolean(query.trim()) || results.length > 0);

  return (
    <form ref={rootRef} className="topbar-search" role="search" onSubmit={handleSubmit}>
      {icon}
      <input
        ref={inputRef}
        type="search"
        className="topbar-search-input"
        placeholder="Search"
        value={query}
        autoComplete="off"
        aria-expanded={showResults}
        aria-controls="topbar-search-results"
        onFocus={() => {
          if (query.trim() || results.length > 0 || loading) setOpen(true);
        }}
        onChange={(event) => handleInput(event.target.value)}
        onKeyDown={handleKeyDown}
      />

      {showResults && (
        <div id="topbar-search-results" className="topbar-search-results">
          {loading && <p className="search-result-status">Searching...</p>}
          {!loading && query && results.length === 0 && (
            <p className="search-result-status">No results for "{query}"</p>
          )}
          {groupedResults.map((group) => (
            <div key={group.type} className="search-result-group">
              <h3 className="search-result-group-title">{group.label}</h3>
              {group.items.map((item) => {
                const isActive = item.id === activeResultId;
                const meta = formatSearchMeta(item);

                return (
                  <button
                    key={item.id}
                    ref={(element) => {
                      resultRefs.current[item.id] = element;
                    }}
                    type="button"
                    className={`search-result-item${isActive ? " search-result-item--active" : ""}`}
                    onClick={() => selectItem(item)}
                    onFocus={() => setActiveResultId(item.id)}
                    onMouseEnter={() => setActiveResultId(item.id)}
                  >
                    <img
                      className="search-result-thumb"
                      src={itemArtworkSrc(item, "poster", { width: 40 })}
                      alt=""
                    />
                    <div className="search-result-info">
                      <span className="search-result-title">{item.attributes.title}</span>
                      {meta && (
                        <span className="search-result-meta">{meta}</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </form>
  );
}
